<template>
  <header class="app-header">
    <h1>Can't Stop</h1>
    <!-- Nothing in here is usable from behind the gate, and a stale
         saved game in localStorage was enough to show a locked-out
         visitor a Leave button. The title stays; the controls go. -->
    <div v-if="isApproved" class="app-header__right">
      <AdminPanel v-if="isAdmin" />
      <button
        class="icon-button"
        title="How to play"
        aria-label="How to play"
        @click="showHowTo = true"
      >?</button>
      <span v-if="roomMeta?.roomCode && game" class="header-code">{{ roomMeta.roomCode }}</span>
      <button
        v-if="game || roomId"
        class="icon-button"
        :title="leaveLabel"
        :aria-label="leaveLabel"
        @click="confirmLeave"
      >
        <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
          <path d="M2.5 12a5.5 5.5 0 1 0 .5-4.5H5v1H1.5V5h1v1.6A6.5 6.5 0 1 1 1.5 8h1A5.5 5.5 0 0 0 2.5 12z" />
        </svg>
      </button>
    </div>
  </header>

  <!-- A live game always wins, so someone rejoining lands straight back at
       the table rather than on a menu. -->
  <SiteGate v-if="!isApproved" />
  <ScoreSheet v-else-if="isFinished" />
  <GameTable v-else-if="game" />
  <OnlineLobby v-else-if="roomId || wantsOnline" @back="wantsOnline = false" />
  <NewGame v-else @play-online="wantsOnline = true" />

  <HowToPlay v-if="showHowTo" @close="dismissHowTo" />

  <!--
    Tap the stamp for what the screen actually measures. Three rounds of
    "there is dead space at the bottom" were guesswork because nobody could
    read the numbers off the phone in the room. Now anyone can.
  -->
  <!-- Tap reloads the app (an installed PWA has no reload button); press
       and hold is the viewport measurement, which is a debugging tool and
       not what a tap should do. -->
  <p
    class="build-stamp"
    role="button"
    tabindex="0"
    title="Tap to reload · hold to measure"
    @click="stampTap"
    @pointerdown="stampHold"
    @pointerup="stampRelease"
    @pointerleave="stampRelease"
    @pointercancel="stampRelease"
  >{{ measured || (refreshing ? 'reloading…' : stamp) }}</p>
  <BugReportButton v-if="firebaseConfigured" />
  <BugResolutionNotice v-if="firebaseConfigured" />
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import GameTable from '@/components/game/GameTable.vue';
import ScoreSheet from '@/components/game/ScoreSheet.vue';
import NewGame from '@/components/lobby/NewGame.vue';
import OnlineLobby from '@/components/lobby/OnlineLobby.vue';
import SiteGate from '@/components/auth/SiteGate.vue';
import AdminPanel from '@/components/auth/AdminPanel.vue';
import HowToPlay from '@/components/shared/HowToPlay.vue';
import BugReportButton from '@/components/shared/BugReportButton.vue';
import BugResolutionNotice from '@/components/shared/BugResolutionNotice.vue';
import { buildStamp, forceRefresh } from '@/utils/buildStamp';
import { viewportLine } from '@/utils/viewport';
import { initUpdateCheck } from '@/utils/checkForUpdate';

const HOWTO_SEEN_KEY = 'cant-stop:howto-seen';

export default {
  name: 'App',
  components: {
    GameTable, ScoreSheet, NewGame, OnlineLobby, SiteGate, AdminPanel,
    BugReportButton, BugResolutionNotice, HowToPlay,
  },
  data() {
    return {
      stamp: buildStamp(),
      measured: '',
      refreshing: false,
      held: false,
      wantsOnline: false,
      pendingJoinCode: null,
      showHowTo: false,
    };
  },
  computed: {
    ...mapState(['game', 'roomId', 'roomMeta', 'firebaseConfigured']),
    ...mapGetters(['isFinished', 'isApproved', 'isAdmin']),
    leaveLabel() {
      return this.roomId ? 'Leave this game' : 'Abandon this game';
    },
  },
  watch: {
    isFinished: {
      // Lifetime stats. `immediate` matters as much as the watch: a client
      // that mounts straight into a finished game - a refresh on the score
      // sheet, or a resumed save that had already ended - never sees this
      // flag CHANGE, and that game would never be counted. Recording is
      // idempotent, so firing on every arrival is free.
      handler(done) { if (done) this.$store.dispatch('recordMyStats'); },
      immediate: true,
    },
    // A shared link (?join=FROG) drops someone straight into the room the
    // moment they're signed in and approved - no code to re-type, which is
    // the whole point of sending a link rather than four letters.
    isApproved(approved) {
      if (approved) this.consumeJoinCode();
    },
  },
  created() {
    const joinCode = new URLSearchParams(window.location.search).get('join');
    if (joinCode) {
      this.pendingJoinCode = joinCode;
      this.wantsOnline = true;
    }
  },
  mounted() {
    this.$store.dispatch('initAuth');

    // First-ever visit: open the walkthrough unprompted, so a friend arriving
    // from a join link learns the lay of the land before the lobby. Marked
    // seen on DISMISS, not on open. A saved game means they've already
    // played, so no auto-open. The ? button brings it back any time.
    if (!localStorage.getItem(HOWTO_SEEN_KEY) && !this.$store.state.hasSavedGame) {
      this.showHowTo = true;
    }

    // House standard (Matt): every app gets Cinema Roll's new-version
    // auto-reload. Never fires mid-game - see appUpdate.js's
    // isSafeMomentForReload, which this app passes `atTable` to.
    initUpdateCheck(this.$store);

    if (this.isApproved) this.consumeJoinCode();

    // A local game in progress survives a refresh. Never for an online game -
    // that state lives in the room, and resuming a stale local copy over it
    // would be wrong.
    if (this.$store.state.hasSavedGame && !this.game && !this.roomId) {
      this.$store.dispatch('resumeGame');
    }
  },
  methods: {
    toggleMeasure() {
      this.measured = this.measured ? '' : viewportLine();
    },
    // Hold for the measurement; a tap is a reload.
    stampHold() {
      this.held = false;
      this.holdTimer = setTimeout(() => { this.held = true; this.toggleMeasure(); }, 550);
    },
    stampRelease() {
      if (this.holdTimer) clearTimeout(this.holdTimer);
      this.holdTimer = null;
    },
    stampTap() {
      if (this.held) { this.held = false; return; }
      if (this.measured) { this.measured = ''; return; }
      this.refreshing = true;
      forceRefresh();
    },
    dismissHowTo() {
      this.showHowTo = false;
      try { localStorage.setItem(HOWTO_SEEN_KEY, '1'); } catch { /* storage off */ }
    },
    consumeJoinCode() {
      if (!this.pendingJoinCode) return;
      const code = this.pendingJoinCode;
      this.pendingJoinCode = null;
      this.$store.dispatch('joinRoomByCode', code);
    },
    confirmLeave() {
      const online = Boolean(this.roomId);
      const message = online
        ? 'Leave this game? The others carry on without you.'
        : 'Abandon this game?';
      // eslint-disable-next-line no-alert -- a deliberate, single destructive confirm
      if (!window.confirm(message)) return;
      if (online) {
        this.$store.dispatch('leaveRoom');
        this.wantsOnline = false;
      } else {
        this.$store.dispatch('abandonGame');
      }
    },
  },
};
</script>
